import { Certification, CertificationStatus, Module } from 'types/admin/modules';
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

export type CertificationsData = Pick<Module, 'certifications'>;
type State = CertificationsData & { initializing: boolean, loading: boolean };

type Actions = {
  setInitialCertifications: (certifications: Certification[]) => void;
  addCertification: () => void;
  deleteCertification: (id: string) => void;
  updateCertificationTitle: (id: string, title: string) => void;
  updateCertificationStatus: (id: string, status: CertificationStatus) => void;
  updateCertification: (certification: Certification) => void;
  reset: () => void;
  setLoadingInitialData: (initializing: boolean) => void;
  setLoading: (loading: boolean) => void;
};

export const useCertificationsStore = create<State & Actions>((set, get, store) => ({
  certifications: [],
  initializing: false,
  loading: false,
  setInitialCertifications: (certifications: Certification[]) => set((state: State) => ({ certifications })),
  addCertification: () => set((state: State) => ({ certifications: [...state.certifications, emptyCertification()] })),
  deleteCertification: (id: string) =>
    set((state: State) => ({ certifications: state.certifications.filter((certification) => certification.id !== id) })),
  updateCertificationTitle: (id: string, title: string) =>
    set((state: State) => ({
      certifications: state.certifications.map((certification) => (certification.id !== id ? certification : { ...certification, title })),
    })),
  updateCertificationStatus: (id: string, status: CertificationStatus) =>
    set((state: State) => ({
      certifications: state.certifications.map((certification) => (certification.id !== id ? certification : { ...certification, status })),
    })),
  updateCertification: (certification: Certification) =>
    set((state: State) => ({
      certifications: state.certifications.map((c) => (c.id !== certification.id ? c : certification)),
    })),
  reset: () => set(store.getInitialState()),
  setLoadingInitialData: (initializing: boolean) => set(() => ({ initializing })),
  setLoading: (loading: boolean) => set(() => ({loading}))
}));

const emptyCertification: () => Certification = () => ({
  id: uuidv4(),
  title: 'New certification',
  shortDescription: '',
  fullDescription: '',
  enterpoint: '',
  status: 'Hidden',
  prerequisites: [],
  numberOfQuestions: 0,
});
